import {useFormik} from 'formik'
import invariant from 'invariant'
import {useNavigate} from 'react-router'

import {useAppSelector} from '../../hooks/redux'

import * as dynamicProductionTimeApi from '../../services/api/dynamic-production-time'
import {goToDynamicProductionTimes} from '../../services/navigation'
import {formDataToDynamicProductionTimeRequest} from '../forms/dynamic-production-time/utils'
import {toValidationSchema} from '../utils/form'

import chevronLeftIcon from '../../../../commons/src/assets/icons/chevron-left.svg'

import ActionBar from '../../../../commons/src/components/action-bar'
import Button from '../../../../commons/src/components/button'
import Grid from '../../../../commons/src/components/grid'
import GridCell from '../../../../commons/src/components/grid/GridCell'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import Stack from '../../../../commons/src/components/stack'
import Container from '../../components/container'
import PageContentLayout from '../../components/page-content-layout'
import PageHeader from '../../components/page-content-layout/PageHeader'

import DynamicProductionTimeForm, {
  SingleFormData,
  initialValues,
  validationSchema,
} from '../forms/dynamic-production-time/form'
import PagePartial from '../page-partial'
import SaveButtonPartial from '../save-button-partial'

import ProductionTimeCalculatorPartial from './production-time-calculator-partial'

const CreateDynamicProductionTimePage = () => {
  const provider = useAppSelector(state => state.core.provider)

  invariant(provider, 'Provider is missing.')

  const navigate = useNavigate()

  const formik = useFormik<SingleFormData>({
    initialValues,
    validationSchema: toValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const dynamicProductionTimeRequest = formDataToDynamicProductionTimeRequest(values, provider)

      try {
        await dynamicProductionTimeApi.create(dynamicProductionTimeRequest)
        goToDynamicProductionTimes(navigate)
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  return (
    <PagePartial hideFooter>
      <PageContentLayout
        header={
          <PageHeader
            headline={
              <ActionBar>
                <Button
                  hierarchy="secondary"
                  icon={<Icon source={chevronLeftIcon} />}
                  onClick={() => goToDynamicProductionTimes(navigate)}
                />
                <Headline
                  size="l"
                  label="Create new dynamic production time"
                  className="u-no-margin-bottom"
                />
              </ActionBar>
            }
          />
        }
      >
        <Container size="l">
          <Grid>
            <GridCell l={6}>
              <Stack space="300">
                <DynamicProductionTimeForm formik={formik} />
                <SaveButtonPartial formik={formik} />
              </Stack>
            </GridCell>
            <GridCell s={0} l={6}>
              <ProductionTimeCalculatorPartial
                dynamicProductionTimeRequest={formDataToDynamicProductionTimeRequest(
                  formik.values,
                  provider
                )}
              />
            </GridCell>
          </Grid>
        </Container>
      </PageContentLayout>
    </PagePartial>
  )
}

export default CreateDynamicProductionTimePage
